import React, { useEffect, useState } from "react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import ScrollReveal from "../components/common/ScrollReveal";
import { useCart } from "../context/CartContext";
import styles from "./ProductsPage.module.css";
import wholesaleImage from "../assets/Products/wholesale-1.jpg";

// --- Product Card Component ---
const ProductCard = ({ product, index }) => {
  const { addToCart } = useCart();

  // Disable button if price is missing or zero
  const isButtonDisabled = !product.price || product.price === 0;

  const handleBuyClick = () => {
    if (isButtonDisabled) return;

    addToCart({
      id: product._id,
      name: product.name,
      price: `${product.price.toLocaleString("en-US")} Br`,
      img: `${product.imageURL}`,
      minQuantity: product.minQuantity || 1,
      maxQuantity: product.maxQuantity || 10,
    });
  };

  return (
    <ScrollReveal className={styles.productCard} delay={index * 0.1}>
      <div className={styles.productImageContainer}>
        {product.imageURL ? (
          <img src={`${product.imageURL}`} alt={product.name} />
        ) : (
          <div
            style={{
              background: "#111",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#555",
            }}
          >
            No Image
          </div>
        )}
      </div>
      <div className={styles.productInfo}>
        <h3>{product.name}</h3>

        {/* --- UPDATE: Min Order Text to White for Luxury Theme --- */}
        {product.minQuantity > 1 && (
          <span
            style={{
              fontSize: "0.85rem",
              color: "#fff", // Changed from Orange to White
              display: "block",
              marginBottom: "8px",
              fontFamily: "'Source Serif 4', serif",
              fontStyle: "italic",
              opacity: 0.8,
            }}
          >
            Min Order: {product.minQuantity}
          </span>
        )}

        <button
          className={styles.buyProductBtn}
          onClick={handleBuyClick}
          disabled={isButtonDisabled}
        >
          {isButtonDisabled ? "Unavailable" : "Buy"}
        </button>
        <div className={styles.productPrice}>
          <span className={styles.priceValue}>
            {isButtonDisabled
              ? "N/A"
              : `${product.price.toLocaleString("en-US")} Br`}
          </span>
          {product.description && (
            <span className={styles.priceDescription}>
              {product.description}
            </span>
          )}
        </div>
      </div>
    </ScrollReveal>
  );
};

function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // --- Fetch Data from Backend ---
  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch("/api/cms/products");
        if (!response.ok) throw new Error("Failed to fetch products.");
        const data = await response.json();

        // Only show items where type is "Product" (ignore Menu items)
        const productItemsOnly = data.filter(
          (p) => p.category?.type === "Product"
        );
        setProducts(productItemsOnly);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // --- Group Products by Category ---
  const groupedProducts = products.reduce((groups, product) => {
    const categoryName = product.category?.name || "Uncategorized";
    if (!groups[categoryName]) {
      groups[categoryName] = [];
    }
    groups[categoryName].push(product);
    return groups;
  }, {});

  // --- Sort Categories ---
  const preferredOrder = [
    "Roasted Coffee",
    "Brewing Equipments",
    "Merch & Books",
  ];

  const sortedCategoryNames = Object.keys(groupedProducts).sort((a, b) => {
    const indexA = preferredOrder.indexOf(a);
    const indexB = preferredOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className={styles.productsPage}>
      <Navbar />
      <div className={styles.productsHero}>
        <h1>Products</h1>
      </div>

      {isLoading ? null : (
        <>
          {sortedCategoryNames.map((categoryName) => {
            const categoryProducts = groupedProducts[categoryName];
            const gridClass =
              categoryName === "Roasted Coffee" ? "grid32Col" : "grid4Col";

            return (
              <section className={styles.productsSection} key={categoryName}>
                {/* Category Title */}
                <h2>{categoryName}</h2>

                {/* Products Grid */}
                <div className={`${styles.productsGrid} ${styles[gridClass]}`}>
                  {categoryProducts.map((product, index) => (
                    <ProductCard
                      product={product}
                      key={product._id}
                      index={index}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}

      {/* Wholesale Section */}
      <ScrollReveal>
        <section
          className={`${styles.productsSection} ${styles.wholesaleSection}`}
        >
          <h2>Wholesales</h2>
          <div className={styles.wholesaleContent}>
            <div className={styles.wholesaleImageContainer}>
              <img src={wholesaleImage} alt="Wholesale coffee" />
            </div>
            <div className={styles.wholesaleText}>
              <p>
                We are proud to partner with businesses by offering premium
                wholesale coffee services tailored to meet your unique needs.
              </p>
              <p className={styles.wholesaleContactInfo}>
                For Wholesale service call <br />
                <span>(+251)92-422-22-00</span>
              </p>
              <h4>
                Partner with us for wholesale services that deliver exceptional
                flavor and quality every time
              </h4>
            </div>
          </div>
        </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
}

export default ProductsPage;
